import { format } from "date-fns";
import { useCallback, useState } from "react";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { api } from "@/services/api";
import type { PricesResponse } from "@/types/api";
import { getTotalGuests } from "@/utils/booking";
import { logError } from "@/utils/logError";
import { useCurrency } from "./useCurrency";

interface UsePricesReturn {
  loading: boolean;
  prices: PricesResponse | null;
  fetchPrices: () => Promise<PricesResponse>;
}

export function usePrices(): UsePricesReturn {
  const { state, actions } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const { currencyCode } = useCurrency();
  const [loading, setLoading] = useState(false);

  const fetchPrices = useCallback(async (): Promise<PricesResponse> => {
    if (
      !campgroundData.urls?.get_prices ||
      !state.dateFrom ||
      !state.dateUntil ||
      !state.selectedCampsite
    ) {
      throw new Error("Missing required parameters for fetching prices");
    }

    setLoading(true);

    try {
      const dateFromStr = format(state.dateFrom, "yyyy-MM-dd");
      const dateUntilStr = format(state.dateUntil, "yyyy-MM-dd");

      const response = await api.getPrices(campgroundData.urls.get_prices, {
        date_from: dateFromStr,
        date_until: dateUntilStr,
        currency_alt: currencyCode,
        persons: getTotalGuests(state.selectedPersons, campgroundData.campground.persons, "human"),
        pets: getTotalGuests(state.selectedPersons, campgroundData.campground.persons, "pet"),
        acco_id: state.selectedCampsite.id,
      });

      actions.setPrices(response);
      setLoading(false);
      return response;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logError(error);
      setLoading(false);
      throw error;
    }
  }, [
    campgroundData,
    state.dateFrom,
    state.dateUntil,
    state.selectedCampsite,
    state.selectedPersons,
    currencyCode,
    actions,
  ]);

  return {
    loading,
    prices: state.prices,
    fetchPrices,
  };
}
